// Sub-Step 3.3 Phase 2 — useViewTracking Hook.
//
// Schickt EINEN POST /user-scrollys/:pk/view sobald der User 25% der
// Page gescrollt hat. Pro Tab-Session (sessionStorage) nur einmal —
// kein Re-Scroll-Doppel-Post.
//
// Backend: @Public, mit Body { sessionId, readPercent }. Backend
// inkrementiert views-Counter nur wenn readPercent >= 0.25, plus
// dedupliziert via (publicKey, sessionId) — Frontend-side dedup ist
// also defense-in-depth (spart Backend-Roundtrip), nicht Korrektheit.
//
// Anonymous + authenticated funktionieren gleich — keine Token-Pflicht
// auf diesem Endpoint (siehe engagement.controller.ts:46 `@Public()`).
//
// useEffect-Audit (Phase-1-Lesson):
//   - Scroll-Listener mit `passive: true` (Smooth-Scroll auf Mobile)
//   - Cleanup beim Unmount (removeEventListener)
//   - useRef-Guards: `triggeredRef` verhindert mehrfaches POST nach Threshold,
//     `sessionIdRef` cached die sessionId für ganze Lifetime
//   - Dependency-Array: nur [publicKey] — wenn sich der ändert (Page-Wechsel),
//     wird die ganze Subscription neu aufgesetzt mit eigener triggeredRef

import { useEffect, useRef } from "react";

const VIEW_THRESHOLD = 0.25; // 25% Page-Scroll
const STORAGE_KEY_PREFIX = "scrolly:view-tracked:";
const SESSION_ID_KEY = "scrolly:view-session-id";

/**
 * Holt oder generiert eine Tab-Session-ID via sessionStorage.
 * Geteilt zwischen allen scrolly-Views in dieser Tab-Session.
 * crypto.randomUUID() ist Standard in modernen Browsern (>2022).
 */
export function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(SESSION_ID_KEY, fresh);
    return fresh;
  } catch {
    // SafariPrivateMode-Fallback: in-memory ID, lokal nur für diesen Mount.
    return `mem-${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Tracking-Dedup-Check: schon mal getrackt in dieser Session?
 */
function hasTrackedThisSession(publicKey: string): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY_PREFIX + publicKey) === "1";
  } catch {
    return false;
  }
}

function markTrackedThisSession(publicKey: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY_PREFIX + publicKey, "1");
  } catch {
    // Silent fallback — in-memory dedup übernimmt via triggeredRef
  }
}

/**
 * Berechnet Scroll-Progress als 0..1.
 * Bei kurzen Pages (< viewportHeight) → 1.0 ab dem ersten Scroll.
 */
export function computeScrollPercent(): number {
  if (typeof window === "undefined") return 0;
  const scrollTop =
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0;
  const viewportHeight = window.innerHeight || 0;
  const totalHeight = document.documentElement.scrollHeight || 0;
  const scrollable = Math.max(0, totalHeight - viewportHeight);
  if (scrollable === 0) {
    // Page passt komplett in Viewport — 25% gilt als sofort erfüllt
    // sobald der Page mountet, also instant trigger.
    return 1;
  }
  return Math.min(1, Math.max(0, scrollTop / scrollable));
}

interface UseViewTrackingProps {
  /** publicKey des Scrollys. Wenn leer/undefined: kein Tracking. */
  publicKey: string | undefined | null;
  /**
   * Optional: Override-Threshold (Default 0.25). Wenn Renderer eine
   * eigene Progress-Quelle hat (z.B. Scrolly-Engine-internal-Progress),
   * kann der Hook anders gefüttert werden — heute reicht Page-Scroll.
   */
  threshold?: number;
  /** Disable-Flag für Test-Stubs / OG-Render / Embed. */
  disabled?: boolean;
}

/**
 * Hook: triggert genau einen POST /user-scrollys/:pk/view sobald der
 * User die Threshold-Marke (default 25%) überschreitet.
 *
 * @example
 * ```tsx
 * // In der Viewer-Page-Komponente
 * useViewTracking({ publicKey: resolved.publicKey });
 * ```
 */
export function useViewTracking(props: UseViewTrackingProps): void {
  const { publicKey, threshold = VIEW_THRESHOLD, disabled = false } = props;
  // useRef-Guards (Phase-1-Lesson):
  // - triggeredRef: einmaliger POST pro Mount-Lifecycle
  // - sessionIdRef: cached, regenerate-prevention
  const triggeredRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (disabled) return;
    if (!publicKey) return;
    // Wenn schon in dieser Tab-Session getrackt → kein Listener, kein POST.
    if (hasTrackedThisSession(publicKey)) return;

    // Reset Refs für neuen publicKey (z.B. Client-Routing zu anderem Scrolly)
    triggeredRef.current = false;
    sessionIdRef.current ??= getOrCreateSessionId();

    const checkAndTrigger = () => {
      if (triggeredRef.current) return;
      const percent = computeScrollPercent();
      if (percent < threshold) return;
      // Threshold erreicht — sofort markieren um Re-Trigger im
      // gleichen Tick zu verhindern (passive scroll-listener feuert
      // mehrfach pro Frame).
      triggeredRef.current = true;
      markTrackedThisSession(publicKey);

      const body = JSON.stringify({
        sessionId: sessionIdRef.current,
        readPercent: percent,
      });
      // Fire-and-forget — kein .then() oder .catch() Logging
      // ist gewollt: Backend-Errors sollen User nicht beeinflussen.
      // Plus: navigator.sendBeacon wäre robuster gegen Page-Unload,
      // aber wir brauchen Content-Type: application/json was sendBeacon
      // nicht direkt unterstützt → fetch + keepalive.
      void fetch(
        `/user-scrollys/${encodeURIComponent(publicKey)}/view`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        },
      ).catch(() => {
        // Network-Error: nicht recovern. Counter wird beim nächsten
        // Reload neu berechnet (Backend liefert frische counts).
      });
    };

    // Initial-Check: möglicherweise ist die Page schon weit gescrollt
    // (Resume-State, Hash-Anchor). Sofort prüfen, nicht erst auf scroll.
    checkAndTrigger();

    if (triggeredRef.current) return; // bereits getriggert beim Initial-Check

    const onScroll = () => checkAndTrigger();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [publicKey, threshold, disabled]);
}
